import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiService } from '../../../../services/api';
import { Eye, Edit, Trash2, X, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSelector } from 'react-redux';
import isAdmin from '../../../../../src/comp/Utils/isAdmin';

// Import the constants for all cabinet types
import { 
  fixedPriceItems, 
  pvcRollingShutterSizes, 
  profileHandlePrices, 
  colorCodeData,
  cabinetTypes,
  shakerData 
} from '../data/constants';

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingOrder, setUpdatingOrder] = useState(null);
  
  const user = useSelector(state => state.user);
  const isAdminUser = isAdmin(user?.role);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      let result;
      
      if (isAdminUser) {
        // Admin gets all orders
        result = await apiService.getAllOrders();
      } else {
        // Regular user gets only their orders
        result = await apiService.getUserOrders();
      }
      
      if (result.success) {
        setOrders(result.data);
      } else {
        toast.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Fetch orders error:', error);
      toast.error('Error fetching orders');
    } finally {
      setLoading(false);
    }
  };

  // UPDATED Cabinet Total Calculation - INCLUDES ALL CABINET TYPES
  const calculateCabinetTotal = (cabinet, country) => {
    if (country === 'Shaker') {
      const cost = parseFloat(cabinet.unitCost) || 0;
      const qty = parseFloat(cabinet.qty) || 1;
      const discount = parseFloat(cabinet.discount) || 0;
      const totalCost = cost * qty;
      const discountAmount = (totalCost * discount) / 100;
      
      return Math.max(totalCost - discountAmount, 0);
    }

    // Check if it's Profile Handle
    if (cabinet.type === "Profile Handle") {
      const qty = parseFloat(cabinet.qty) || 1;
      const profileHandleInches = cabinet.profileHandleInches || "6";
      
      // Get the price from profileHandlePrices based on inches
      const pricePerInch = profileHandlePrices[profileHandleInches] || 0;
      
      // Multiply by 4 as requested
      const priceAfter4x = pricePerInch * 4;
      
      // Calculate total cost before discount
      const totalCostBeforeDiscount = priceAfter4x * qty;
      
      // Apply discount
      const discount = parseFloat(cabinet.discount) || 0;
      const discountAmount = (totalCostBeforeDiscount * discount) / 100;
      
      // Final total after discount (in rupees)
      const finalTotalRupees = Math.max(totalCostBeforeDiscount - discountAmount, 0);
      
      // Convert to dollars (assuming 1 USD = 89 INR)
      const finalTotalInDollars = finalTotalRupees / 89;
      
      return finalTotalInDollars;
    }

    // Check if it's PVC Rolling Shutter
    if (cabinet.type === "PVC Rolling Shutter") {
      const unitCost = parseFloat(cabinet.unitCost) || 0;
      const qty = parseFloat(cabinet.qty) || 1;
      const discount = parseFloat(cabinet.discount) || 0;
      const totalCost = unitCost * qty;
      const discountAmount = (totalCost * discount) / 100;
      
      return Math.max(totalCost - discountAmount, 0);
    }

    // Check if it's a fixed price item
    const isFixedPriceItem = fixedPriceItems[cabinet.type];
    
    if (isFixedPriceItem) {
      // Fixed price item calculation
      const unitCost = fixedPriceItems[cabinet.type] || 0;
      const qty = parseFloat(cabinet.qty) || 1;
      const discount = parseFloat(cabinet.discount) || 0;
      const totalCost = unitCost * qty;
      const discountAmount = (totalCost * discount) / 100;
      
      return Math.max(totalCost - discountAmount, 0);
    }

    // Regular Custom Cabinet Calculation
    const height = parseFloat(cabinet.height) || 0;
    const width = parseFloat(cabinet.width) || 0;
    const unitCost = parseFloat(cabinet.unitCost) || 0;
    const qty = parseFloat(cabinet.qty) || 1;
    const drawerSlides = parseFloat(cabinet.drawerSlides) || 0;
    const hinges = parseFloat(cabinet.hinges) || 0;
    const discount = parseFloat(cabinet.discount) || 0;
    
    // Check if all required fields are filled
    const hasRequiredFields = cabinet.type && height > 0 && width > 0 && cabinet.color && unitCost > 0;
    
    if (!hasRequiredFields) {
      return 0;
    }

    // Convert inches to feet
    const heightInFeet = height / 12;
    const widthInFeet = width / 12;
    
    // Calculate square feet
    const sqft = heightInFeet * widthInFeet;
    
    // Calculate shutter price using the color's specific price
    const shutterCost = sqft * unitCost;
    
    // Carcass price (fixed ₹4860 per cabinet)
    const carcassPrice = 4860;
    
    // Drawer Slides charges (₹250 per unit)
    const drawerSlidesCost = drawerSlides * 250;
    
    // Hinges charges (₹85 per unit)
    const hingesCost = hinges * 85;
    
    // Calculate total cost before discount
    const totalCostBeforeDiscount = (shutterCost + carcassPrice + drawerSlidesCost + hingesCost) * 4 * qty;
    
    // Apply discount
    const discountAmount = (totalCostBeforeDiscount * discount) / 100;
    
    // Final total after discount
    const finalTotal = Math.max(totalCostBeforeDiscount - discountAmount, 0);
    
    // Convert to dollars (assuming 1 USD = 89 INR)
    const finalTotalInDollars = finalTotal / 89;
    
    return finalTotalInDollars;
  };

  // Get material and unit cost based on selected color
  const getMaterialAndCost = (colorCode) => {
    if (!colorCodeData || !colorCodeData[colorCode]) {
      return { material: "", unitCost: "" };
    }
    
    const colorInfo = colorCodeData[colorCode];
    
    if (typeof colorInfo === 'object') {
      return {
        material: colorInfo.material || "",
        unitCost: colorInfo.unitCost || colorInfo.shutterPrice || ""
      };
    }
    
    return {
      material: "",
      unitCost: colorInfo || ""
    };
  };

  // UPDATED PDF Generation Function with All Cabinet Types
  const generateOrderPDF = (order) => {
    try {
      const doc = new jsPDF();
      const taxRate = 6.625;
      
      // Calculate sales tax and totals
      const salesTax = (order.totalCost * taxRate) / 100;
      const finalTotal = order.totalCost + salesTax;

      let yPos = 20;

      // === HEADER ===
      doc.setTextColor(59, 130, 246);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('LAMINANCE CABINETRY', 105, yPos, { align: 'center' });
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.text('418 Rt 23, Franklin, NJ, 07416', 105, yPos + 8, { align: 'center' });
      doc.text('Phone: (862) 450-6069', 105, yPos + 16, { align: 'center' });
      
      yPos += 30;

      // === INVOICE HEADER ===
      doc.setFillColor(79, 70, 229);
      doc.rect(10, yPos, 190, 12, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text(`INVOICE - ${order.invoiceId}`, 105, yPos + 8, { align: 'center' });
      
      yPos += 20;

      // === ORDER & CUSTOMER INFO ===
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      
      // Left Column - Order Info
      doc.setFont(undefined, 'bold');
      doc.text('ORDER INFORMATION:', 15, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(`Invoice ID: ${order.invoiceId}`, 15, yPos + 8);
      doc.text(`Order Date: ${new Date(order.createdAt).toLocaleDateString()}`, 15, yPos + 16);
      doc.text(`Order Type: ${order.country}`, 15, yPos + 24);
      doc.text(`Status: ${order.status}`, 15, yPos + 32);

      // Right Column - Customer Info
      doc.setFont(undefined, 'bold');
      doc.text('CUSTOMER INFORMATION:', 110, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(`Name: ${order.customerInfo.firstName} ${order.customerInfo.lastName}`, 110, yPos + 8);
      doc.text(`Email: ${order.customerInfo.email}`, 110, yPos + 16);
      doc.text(`Phone: ${order.customerInfo.phone}`, 110, yPos + 24);
      doc.text(`Address: ${order.customerInfo.address}`, 110, yPos + 32);
      doc.text(`City: ${order.customerInfo.city}, State: ${order.customerInfo.state} ${order.customerInfo.zip}`, 110, yPos + 40);

      yPos += 50;

      // === TABLE HEADER ===
      doc.setFillColor(79, 70, 229);
      doc.rect(10, yPos, 190, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      
      if (order.country === 'Shaker') {
        doc.text('Type', 12, yPos + 5);
        doc.text('Description', 40, yPos + 5);
        doc.text('Qty', 110, yPos + 5);
        doc.text('Discount', 130, yPos + 5);
        doc.text('Total', 150, yPos + 5);
      } else {
        doc.text('Type', 12, yPos + 5);
        doc.text('Size/Width', 30, yPos + 5);
        doc.text('Inches/Height', 50, yPos + 5);
        doc.text('Color', 70, yPos + 5);
        doc.text('Material', 95, yPos + 5);
        doc.text('Qty', 120, yPos + 5);
        doc.text('Drawer', 135, yPos + 5);
        doc.text('Hinges', 150, yPos + 5);
        doc.text('Discount', 165, yPos + 5);
        doc.text('Total', 185, yPos + 5);
      }
      
      yPos += 12;

      // === TABLE ROWS ===
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');

      order.cabinets.forEach((cabinet, index) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        const cabinetTotal = calculateCabinetTotal(cabinet, order.country);
        
        // Alternate row background
        if (index % 2 === 0) {
          doc.setFillColor(245, 245, 245);
          doc.rect(10, yPos - 2, 190, 8, 'F');
        }
        
        // Convert all values to strings
        if (order.country === 'Shaker') {
          doc.text(String(cabinet.type || 'N/A'), 12, yPos + 5);
          doc.text(String((cabinet.description || 'N/A').substring(0, 30)), 40, yPos + 5);
          doc.text(String(cabinet.qty || '1'), 110, yPos + 5);
          doc.text(`${String(cabinet.discount || '0')}%`, 130, yPos + 5);
          doc.text(`$${String(cabinetTotal.toFixed(2))}`, 150, yPos + 5);
        } else {
          const isFixedPriceItem = fixedPriceItems[cabinet.type];
          const isPVCRollingShutter = cabinet.type === "PVC Rolling Shutter";
          const isProfileHandle = cabinet.type === "Profile Handle";
          
          // For Profile Handle, show inches
          if (isProfileHandle) {
            doc.text(String(cabinet.type || 'N/A'), 12, yPos + 5);
            doc.text("Inches:", 30, yPos + 5);
            doc.text(`${String(cabinet.profileHandleInches || '0')}"`, 50, yPos + 5);
            doc.text("N/A", 70, yPos + 5);
            doc.text(String(cabinet.material || 'N/A'), 95, yPos + 5);
            doc.text(String(cabinet.qty || '1'), 120, yPos + 5);
            doc.text("N/A", 135, yPos + 5);
            doc.text("N/A", 150, yPos + 5);
            doc.text(`${String(cabinet.discount || '0')}%`, 165, yPos + 5);
            doc.text(`$${String(cabinetTotal.toFixed(2))}`, 185, yPos + 5);
          }
          // For PVC Rolling Shutter, show size
          else if (isPVCRollingShutter) {
            doc.text(String(cabinet.type || 'N/A'), 12, yPos + 5);
            doc.text("Size:", 30, yPos + 5);
            doc.text(String(cabinet.pvcSize || 'N/A'), 50, yPos + 5);
            doc.text(String(cabinet.color || 'N/A'), 70, yPos + 5);
            doc.text(String(cabinet.material || 'N/A'), 95, yPos + 5);
            doc.text(String(cabinet.qty || '1'), 120, yPos + 5);
            doc.text("N/A", 135, yPos + 5);
            doc.text("N/A", 150, yPos + 5);
            doc.text(`${String(cabinet.discount || '0')}%`, 165, yPos + 5);
            doc.text(`$${String(cabinetTotal.toFixed(2))}`, 185, yPos + 5);
          }
          // For fixed price items
          else if (isFixedPriceItem) {
            doc.text(String(cabinet.type || 'N/A'), 12, yPos + 5);
            doc.text("N/A", 30, yPos + 5);
            doc.text("N/A", 50, yPos + 5);
            doc.text("N/A", 70, yPos + 5);
            doc.text(String(cabinet.material || 'N/A'), 95, yPos + 5);
            doc.text(String(cabinet.qty || '1'), 120, yPos + 5);
            doc.text("N/A", 135, yPos + 5);
            doc.text("N/A", 150, yPos + 5);
            doc.text(`${String(cabinet.discount || '0')}%`, 165, yPos + 5);
            doc.text(`$${String(cabinetTotal.toFixed(2))}`, 185, yPos + 5);
          }
          // For regular cabinets
          else {
            doc.text(String(cabinet.type || 'N/A'), 12, yPos + 5);
            doc.text(`${String(cabinet.width || '0')}"`, 30, yPos + 5);
            doc.text(`${String(cabinet.height || '0')}"`, 50, yPos + 5);
            doc.text(String(cabinet.color || 'N/A'), 70, yPos + 5);
            doc.text(String(cabinet.material || 'N/A'), 95, yPos + 5);
            doc.text(String(cabinet.qty || '1'), 120, yPos + 5);
            doc.text(String(cabinet.drawerSlides || '0'), 135, yPos + 5);
            doc.text(String(cabinet.hinges || '0'), 150, yPos + 5);
            doc.text(`${String(cabinet.discount || '0')}%`, 165, yPos + 5);
            doc.text(`$${String(cabinetTotal.toFixed(2))}`, 185, yPos + 5);
          }
        }
        
        yPos += 10;
      });

      yPos += 10;

      // === TOTALS & TAX BREAKDOWN ===
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      
      // Subtotal
      doc.text(`Subtotal: $${order.totalCost?.toFixed(2) || '0.00'}`, 15, yPos);
      yPos += 8;
      
      // Overall Discount
      if (order.overallDiscount > 0) {
        doc.text(`Overall Discount: ${order.overallDiscount}%`, 15, yPos);
        yPos += 8;
        const discountAmount = (order.totalCost * order.overallDiscount) / 100;
        doc.text(`Discount Amount: -$${discountAmount.toFixed(2)}`, 15, yPos);
        yPos += 8;
      }
      
      // Sales Tax
      doc.setTextColor(59, 130, 246);
      doc.text(`Sales Tax (${taxRate}%): +$${salesTax.toFixed(2)}`, 15, yPos);
      yPos += 8;
      
      // Final Total
      doc.setFontSize(12);
      doc.text(`Final Total: $${finalTotal.toFixed(2)}`, 15, yPos);
      yPos += 15;

      // === FOOTER ===
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.text('Generated on: ' + new Date().toLocaleDateString(), 15, yPos);
      doc.text('Thank you for your business!', 105, yPos, { align: 'center' });

      // Save PDF
      const fileName = `${order.invoiceId}_${order.customerInfo.firstName}_${order.customerInfo.lastName}.pdf`;
      doc.save(fileName);
      return true;
    } catch (error) {
      console.error('PDF Generation Error:', error);
      throw new Error('Failed to generate PDF: ' + error.message);
    }
  };

  // Download PDF Handler
  const handleDownloadPDF = async (order) => {
    try {
      await generateOrderPDF(order);
      toast.success(`PDF downloaded for ${order.invoiceId}`);
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) {
      return;
    }

    try {
      const result = await apiService.deleteOrder(orderId);
      
      if (result.success) {
        setOrders(prev => prev.filter(order => order._id !== orderId));
        toast.success('Order deleted successfully');
      } else {
        toast.error(result.message || 'Failed to delete order');
      }
    } catch (error) {
      console.error('Delete order error:', error);
      toast.error('Error deleting order');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrder(orderId);
      
      const result = await apiService.updateOrder(orderId, { status: newStatus });
      
      if (result.success) {
        setOrders(prev => prev.map(order => 
          order._id === orderId ? { ...order, status: newStatus } : order
        ));
        toast.success('Order status updated successfully');
      } else {
        toast.error(result.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Update order status error:', error);
      toast.error('Error updating order status');
    } finally {
      setUpdatingOrder(null);
    }
  };

  // Edit Customer Info Function - FIXED
  const editCustomerInfo = (order) => {
    try {
      setSelectedOrder(JSON.parse(JSON.stringify(order)));
      setShowEditModal(true);
      setShowViewModal(false);
      setShowEditDetailsModal(false);
    } catch (error) {
      console.error('Edit customer info error:', error);
      toast.error('Failed to open edit modal');
    }
  };

  // Edit Order Details Function - FIXED
  const editOrderDetails = (order) => {
    try {
      setSelectedOrder(JSON.parse(JSON.stringify(order)));
      setShowEditDetailsModal(true);
      setShowViewModal(false);
      setShowEditModal(false);
    } catch (error) {
      console.error('Edit order details error:', error);
      toast.error('Failed to open edit details modal');
    }
  };

  // View Order Details Function - FIXED
  const viewOrderDetails = (order) => {
    try {
      setSelectedOrder(order);
      setShowViewModal(true);
      setShowEditModal(false);
      setShowEditDetailsModal(false);
    } catch (error) {
      console.error('View order details error:', error);
      toast.error('Failed to open view modal');
    }
  };

  const saveEditedCustomerInfo = async () => {
    try {
      if (!selectedOrder) {
        toast.error('No order selected');
        return;
      }

      const result = await apiService.updateOrder(selectedOrder._id, {
        customerInfo: selectedOrder.customerInfo
      });

      if (result.success) {
        setOrders(prev => prev.map(order => 
          order._id === selectedOrder._id ? result.data : order
        ));
        setShowEditModal(false);
        setSelectedOrder(null);
        toast.success('Customer information updated successfully');
      } else {
        toast.error(result.message || 'Failed to update customer information');
      }
    } catch (error) {
      console.error('Save edited customer info error:', error);
      toast.error('Error updating customer information');
    }
  };

  const saveEditedOrderDetails = async () => {
    try {
      if (!selectedOrder) {
        toast.error('No order selected');
        return;
      }

      // Recalculate total cost before saving
      const updatedTotalCost = selectedOrder.cabinets.reduce((total, cabinet) => {
        return total + calculateCabinetTotal(cabinet, selectedOrder.country);
      }, 0);

      const result = await apiService.updateOrder(selectedOrder._id, {
        cabinets: selectedOrder.cabinets,
        totalCost: updatedTotalCost,
        overallDiscount: selectedOrder.overallDiscount || 0
      });

      if (result.success) {
        setOrders(prev => prev.map(order => 
          order._id === selectedOrder._id ? result.data : order
        ));
        setShowEditDetailsModal(false);
        setSelectedOrder(null);
        toast.success('Order details updated successfully');
      } else {
        toast.error(result.message || 'Failed to update order details');
      }
    } catch (error) {
      console.error('Save edited order details error:', error);
      toast.error('Error updating order details');
    }
  };

  // UPDATED: Handle cabinet field changes with proper dropdown logic
  const updateCabinetField = (cabinetIndex, field, value) => {
    setSelectedOrder(prev => {
      const updatedCabinets = [...prev.cabinets];
      const updatedCabinet = {
        ...updatedCabinets[cabinetIndex],
        [field]: value
      };

      // Handle color changes - update material and unitCost
      if (field === "color" && value) {
        const { material, unitCost } = getMaterialAndCost(value);
        updatedCabinet.material = material;
        // Only update unitCost for regular cabinets, not for PVC Rolling Shutter or Profile Handle
        if (updatedCabinet.type !== "PVC Rolling Shutter" && updatedCabinet.type !== "Profile Handle") {
          updatedCabinet.unitCost = unitCost;
        }
      }

      // Handle type changes
      if (field === "type") {
        // Reset fields when type changes
        if (value === "Profile Handle") {
          updatedCabinet.description = "Profile Handle - Select Inches";
          updatedCabinet.height = "N/A";
          updatedCabinet.width = "N/A";
          updatedCabinet.material = "Profile Handle";
          updatedCabinet.drawerSlides = "";
          updatedCabinet.hinges = "";
          updatedCabinet.color = "";
          updatedCabinet.unitCost = "";
          updatedCabinet.pvcSize = "";
        } else if (value === "PVC Rolling Shutter") {
          updatedCabinet.description = "PVC Rolling Shutter - Select Size";
          updatedCabinet.height = "N/A";
          updatedCabinet.width = "N/A";
          updatedCabinet.material = "PVC Rolling Shutter";
          updatedCabinet.drawerSlides = "";
          updatedCabinet.hinges = "";
          updatedCabinet.color = "";
          updatedCabinet.unitCost = "";
          updatedCabinet.profileHandleInches = "";
        } else if (fixedPriceItems[value] && value !== "PVC Rolling Shutter" && value !== "Profile Handle") {
          updatedCabinet.unitCost = fixedPriceItems[value];
          updatedCabinet.height = "N/A";
          updatedCabinet.width = "N/A";
          updatedCabinet.description = `${value} - Fixed Price Item`;
          updatedCabinet.drawerSlides = "";
          updatedCabinet.hinges = "";
          updatedCabinet.color = "";
          updatedCabinet.material = "";
          updatedCabinet.pvcSize = "";
          updatedCabinet.profileHandleInches = "";
        } else if (prev.country === "Shaker") {
          const shakerItem = shakerData.find((item) => item.item === value);
          if (shakerItem) {
            updatedCabinet.description = shakerItem.description;
            updatedCabinet.unitCost = shakerItem.unitcost;
            updatedCabinet.height = "";
            updatedCabinet.width = "";
            updatedCabinet.color = "";
            updatedCabinet.material = "";
          }
        } else {
          // Regular custom cabinet
          updatedCabinet.description = "";
          updatedCabinet.height = "";
          updatedCabinet.width = "";
          updatedCabinet.color = "";
          updatedCabinet.material = "";
          updatedCabinet.unitCost = "";
          updatedCabinet.drawerSlides = "";
          updatedCabinet.hinges = "";
          updatedCabinet.pvcSize = "";
          updatedCabinet.profileHandleInches = "";
        }
      }

      // Handle PVC Rolling Shutter size selection
      if (field === "pvcSize" && updatedCabinet.type === "PVC Rolling Shutter") {
        const sizePrice = pvcRollingShutterSizes[value] || 0;
        updatedCabinet.unitCost = sizePrice;
        updatedCabinet.description = `PVC Rolling Shutter - ${value}`;
      }

      // Handle Profile Handle inch selection
      if (field === "profileHandleInches" && updatedCabinet.type === "Profile Handle") {
        const inchPrice = profileHandlePrices[value] || 0;
        updatedCabinet.unitCost = inchPrice;
        updatedCabinet.description = `Profile Handle - ${value} inch`;
      }

      updatedCabinets[cabinetIndex] = updatedCabinet;
      return {
        ...prev,
        cabinets: updatedCabinets
      };
    });
  };

  const addCabinetRow = () => {
    setSelectedOrder(prev => ({
      ...prev,
      cabinets: [
        ...prev.cabinets,
        {
          type: "",
          height: "",
          width: "",
          color: "",
          material: "",
          unitCost: "",
          drawerSlides: "0",
          hinges: "0",
          discount: 0,
          description: "",
          qty: 1,
          pvcSize: "",
          profileHandleInches: ""
        }
      ]
    }));
  };

  const removeCabinetRow = (index) => {
    if (selectedOrder.cabinets.length > 1) {
      setSelectedOrder(prev => ({
        ...prev,
        cabinets: prev.cabinets.filter((_, i) => i !== index)
      }));
    } else {
      toast.error('At least one cabinet is required');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      'In Progress': 'bg-purple-100 text-purple-800 border-purple-200',
      Completed: 'bg-green-100 text-green-800 border-green-200',
      Cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTotalItems = (order) => {
    return order.cabinets.reduce((total, cab) => total + (cab.qty || 1), 0);
  };

  // Get cabinet types based on country
  const getCabinetTypes = (country) => {
    if (country === "Shaker") {
      return shakerData.map(item => item.item);
    }
    return cabinetTypes;
  };

  // Get colors based on cabinet type
  const getAvailableColors = (cabinetType) => {
    if (cabinetType === "PVC Rolling Shutter") {
      return ["Aluminium", "Black", "White"];
    }
    return Object.keys(colorCodeData).filter(color => 
      !["Aluminium", "Black", "White"].includes(color)
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.invoiceId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerInfo?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerInfo?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {isAdminUser ? 'All Orders Management' : 'My Orders'}
              </h1>
              <p className="text-gray-600">
                {isAdminUser ? 'View and manage all cabinet orders' : 'View and manage your cabinet orders'}
              </p>
              {isAdminUser && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    Admin Mode - Viewing All Orders
                  </span>
                </div>
              )}
            </div>
            <div className="mt-4 lg:mt-0">
              <button
                onClick={() => window.location.href = '/dashboard/cco'}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                + Create New Order
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-indigo-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-indigo-600">{orders.length}</div>
              <div className="text-sm text-gray-600">Total Orders</div>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {orders.filter(o => o.status === 'Pending').length}
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {orders.filter(o => o.status === 'Completed').length}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                ${orders.reduce((sum, order) => sum + (order.totalCost || 0), 0).toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">Total Revenue</div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Confirmed">Confirmed</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-center justify-end">
              <span className="text-sm text-gray-600">
                Showing {filteredOrders.length} of {orders.length} orders
              </span>
            </div>
          </div>
        </div>

        {/* Orders Grid */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {isAdminUser ? 'No Orders Found' : 'No Orders Yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {isAdminUser ? 'There are no orders in the system yet' : 'Create your first cabinet order to get started'}
            </p>
            <button
              onClick={() => window.location.href = '/dashboard/cco'}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
            >
              Create New Order
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredOrders.map((order) => (
              <div key={order._id} className="bg-white rounded-2xl shadow-lg border border-indigo-100 overflow-hidden hover:shadow-xl transition-all duration-300">
                {/* Order Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-bold text-lg">{order.invoiceId}</h3>
                      <p className="text-indigo-100 text-sm">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="p-4 border-b border-indigo-50">
                  <h4 className="font-semibold text-gray-800 mb-2">Customer</h4>
                  <p className="text-gray-700">
                    {order.customerInfo.firstName} {order.customerInfo.lastName}
                  </p>
                  <p className="text-gray-600 text-sm">{order.customerInfo.email}</p>
                  <p className="text-gray-600 text-sm">{order.customerInfo.phone}</p>
                  {isAdminUser && order.userId && (
                    <p className="text-gray-500 text-xs mt-1">
                      User ID: {order.userId}
                    </p>
                  )}
                </div>

                {/* Order Details */}
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Type</p>
                      <p className="font-semibold text-gray-800">{order.country}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Items</p>
                      <p className="font-semibold text-gray-800">{getTotalItems(order)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Cabinets</p>
                      <p className="font-semibold text-gray-800">{order.cabinets.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total</p>
                      <p className="font-semibold text-green-600">${order.totalCost?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>

                  {/* Cabinet Types Summary */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">Cabinet Types:</p>
                    <div className="flex flex-wrap gap-1">
                      {order.cabinets.slice(0, 3).map((cabinet, index) => (
                        <span key={index} className="bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded">
                          {cabinet.type}
                        </span>
                      ))}
                      {order.cabinets.length > 3 && (
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          +{order.cabinets.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => viewOrderDetails(order)}
                      className="flex-1 bg-blue-500 text-white py-2 px-3 rounded-lg hover:bg-blue-600 transition-colors text-sm font-semibold flex items-center justify-center gap-1"
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button
                      onClick={() => handleDownloadPDF(order)}
                      className="flex-1 bg-green-500 text-white py-2 px-3 rounded-lg hover:bg-green-600 transition-colors text-sm font-semibold flex items-center justify-center gap-1"
                    >
                      <Download size={14} />
                      PDF
                    </button>
                    <button
                      onClick={() => editCustomerInfo(order)}
                      className="flex-1 bg-indigo-500 text-white py-2 px-3 rounded-lg hover:bg-indigo-600 transition-colors text-sm font-semibold flex items-center justify-center gap-1"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => deleteOrder(order._id)}
                      className="flex-1 bg-red-500 text-white py-2 px-3 rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold flex items-center justify-center gap-1"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                  <div className="mt-3">
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                      disabled={updatingOrder === order._id}
                      className="w-full bg-gray-100 border-none rounded-lg text-sm font-semibold text-gray-700 px-3 py-2 disabled:opacity-50"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  {updatingOrder === order._id && (
                    <div className="text-center mt-2">
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                      <span className="text-xs text-gray-500 ml-2">Updating...</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View Order Details Modal */}
        {showViewModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Order Details - {selectedOrder.invoiceId}</h2>
                    <p className="text-indigo-100">Complete order information</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownloadPDF(selectedOrder)}
                      className="bg-white text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors font-semibold flex items-center gap-2"
                    >
                      <Download size={16} />
                      Download PDF
                    </button>
                    <button
                      onClick={() => editOrderDetails(selectedOrder)}
                      className="bg-white text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors font-semibold flex items-center gap-2"
                    >
                      <Edit size={16} />
                      Edit Details
                    </button>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="bg-white text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors font-semibold flex items-center gap-2"
                    >
                      <X size={16} />
                      Close
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {/* Customer Information */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold">{selectedOrder.customerInfo.firstName} {selectedOrder.customerInfo.lastName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold">{selectedOrder.customerInfo.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-semibold">{selectedOrder.customerInfo.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Order Date</p>
                      <p className="font-semibold">{new Date(selectedOrder.customerInfo.date).toLocaleDateString()}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="font-semibold">
                        {selectedOrder.customerInfo.address}, {selectedOrder.customerInfo.city}, {selectedOrder.customerInfo.state} {selectedOrder.customerInfo.zip}
                      </p>
                    </div>
                    {isAdminUser && selectedOrder.userId && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-600">User ID</p>
                        <p className="font-semibold text-purple-600">{selectedOrder.userId}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Order Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Order Type</p>
                      <p className="font-bold text-indigo-600">{selectedOrder.country}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Items</p>
                      <p className="font-bold text-green-600">{getTotalItems(selectedOrder)}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Cabinets</p>
                      <p className="font-bold text-blue-600">{selectedOrder.cabinets.length}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Grand Total</p>
                      <p className="font-bold text-purple-600">${selectedOrder.totalCost?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>

                {/* Cabinets Details */}
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Cabinet Details</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                          {selectedOrder.country === 'Shaker' ? (
                            <>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Qty</th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Size/Width</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Inches/Height</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Color</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Material</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Qty</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Drawer Slides</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Hinges</th>
                            </>
                          )}
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Discount</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.cabinets.map((cabinet, index) => {
                          const isFixedPriceItem = fixedPriceItems[cabinet.type];
                          const isPVCRollingShutter = cabinet.type === "PVC Rolling Shutter";
                          const isProfileHandle = cabinet.type === "Profile Handle";
                          
                          return (
                            <tr key={index}>
                              <td className="px-4 py-3 text-sm text-gray-700">{cabinet.type}</td>
                              {selectedOrder.country === 'Shaker' ? (
                                <>
                                  <td className="px-4 py-3 text-sm text-gray-700">{cabinet.description}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{cabinet.qty}</td>
                                </>
                              ) : (
                                <>
                                  {/* Size/Width Column */}
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {isProfileHandle ? "Inches:" : 
                                     isPVCRollingShutter ? "Size:" : 
                                     isFixedPriceItem ? "N/A" : 
                                     `${cabinet.width}"`}
                                  </td>
                                  {/* Inches/Height Column */}
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {isProfileHandle ? `${cabinet.profileHandleInches}"` : 
                                     isPVCRollingShutter ? (cabinet.pvcSize || "N/A") : 
                                     isFixedPriceItem ? "N/A" : 
                                     `${cabinet.height}"`}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {isFixedPriceItem || isProfileHandle ? "N/A" : cabinet.color}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{cabinet.material}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{cabinet.qty}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {isFixedPriceItem || isPVCRollingShutter || isProfileHandle ? "N/A" : (cabinet.drawerSlides || '0')}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {isFixedPriceItem || isPVCRollingShutter || isProfileHandle ? "N/A" : (cabinet.hinges || '0')}
                                  </td>
                                </>
                              )}
                              <td className="px-4 py-3 text-sm text-gray-700">{cabinet.discount}%</td>
                              <td className="px-4 py-3 text-sm font-semibold text-green-600">
                                ${calculateCabinetTotal(cabinet, selectedOrder.country).toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Customer Information Modal */}
        {showEditModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-t-2xl">
                <h2 className="text-2xl font-bold text-white">Edit Customer Information - {selectedOrder.invoiceId}</h2>
                <p className="text-indigo-100">Update customer details</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      value={selectedOrder.customerInfo.firstName}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          firstName: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      value={selectedOrder.customerInfo.lastName}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          lastName: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={selectedOrder.customerInfo.email}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          email: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={selectedOrder.customerInfo.phone}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          phone: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Address *</label>
                    <textarea
                      value={selectedOrder.customerInfo.address}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          address: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      rows="3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                    <input
                      type="text"
                      value={selectedOrder.customerInfo.city}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          city: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                    <input
                      type="text"
                      value={selectedOrder.customerInfo.state}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          state: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">ZIP Code *</label>
                    <input
                      type="text"
                      value={selectedOrder.customerInfo.zip}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          zip: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Order Date</label>
                    <input
                      type="date"
                      value={selectedOrder.customerInfo.date ? new Date(selectedOrder.customerInfo.date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setSelectedOrder({
                        ...selectedOrder,
                        customerInfo: {
                          ...selectedOrder.customerInfo,
                          date: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedOrder(null);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEditedCustomerInfo}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Order Details Modal */}
        {showEditDetailsModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Edit Order Details - {selectedOrder.invoiceId}</h2>
                    <p className="text-indigo-100">Update cabinet information and pricing</p>
                  </div>
                  <button
                    onClick={addCabinetRow}
                    className="bg-white text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors font-semibold"
                  >
                    + Add Cabinet
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Cabinet Details</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                          {selectedOrder.country === 'Shaker' ? (
                            <>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Qty</th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Width/Size</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Height/Inches</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Color</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Material</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Qty</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Drawer Slides</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Hinges</th>
                            </>
                          )}
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Discount %</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Total</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.cabinets.map((cabinet, index) => {
                          const isFixedPriceItem = fixedPriceItems[cabinet.type];
                          const isPVCRollingShutter = cabinet.type === "PVC Rolling Shutter";
                          const isProfileHandle = cabinet.type === "Profile Handle";
                          const isShaker = selectedOrder.country === 'Shaker';
                          
                          return (
                            <tr key={index}>
                              {/* Type Column with Dropdown */}
                              <td className="px-4 py-3">
                                <select
                                  value={cabinet.type}
                                  onChange={(e) => updateCabinetField(index, 'type', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                  <option value="">Select Type</option>
                                  {getCabinetTypes(selectedOrder.country).map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                  ))}
                                </select>
                              </td>
                              
                              {isShaker ? (
                                <>
                                  <td className="px-4 py-3">
                                    <input
                                      type="text"
                                      value={cabinet.description}
                                      readOnly
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="number"
                                      value={cabinet.qty}
                                      onChange={(e) => updateCabinetField(index, 'qty', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      min="1"
                                    />
                                  </td>
                                </>
                              ) : (
                                <>
                                  {/* Width/Size Column */}
                                  <td className="px-4 py-3">
                                    {isProfileHandle ? (
                                      <span className="text-sm text-gray-500 block text-center">Inches</span>
                                    ) : isPVCRollingShutter ? (
                                      <select
                                        value={cabinet.pvcSize || ''}
                                        onChange={(e) => updateCabinetField(index, 'pvcSize', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      >
                                        <option value="">Select Size</option>
                                        {Object.keys(pvcRollingShutterSizes).map((size) => (
                                          <option key={size} value={size}>
                                            {size} - ${pvcRollingShutterSizes[size]}
                                          </option>
                                        ))}
                                      </select>
                                    ) : isFixedPriceItem ? (
                                      <span className="text-sm text-gray-500 block text-center">N/A</span>
                                    ) : (
                                      <input
                                        type="number"
                                        value={cabinet.width}
                                        onChange={(e) => updateCabinetField(index, 'width', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        placeholder="Width"
                                        min="0"
                                        step="0.1"
                                      />
                                    )}
                                  </td>
                                  
                                  {/* Height/Inches Column */}
                                  <td className="px-4 py-3">
                                    {isProfileHandle ? (
                                      <select
                                        value={cabinet.profileHandleInches || ''}
                                        onChange={(e) => updateCabinetField(index, 'profileHandleInches', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      >
                                        <option value="">Select Inches</option>
                                        {Object.keys(profileHandlePrices).map((inches) => (
                                          <option key={inches} value={inches}>
                                            {inches}" - ${profileHandlePrices[inches]}
                                          </option>
                                        ))}
                                      </select>
                                    ) : isPVCRollingShutter ? (
                                      <span className="text-sm text-gray-500 block text-center">N/A</span>
                                    ) : isFixedPriceItem ? (
                                      <span className="text-sm text-gray-500 block text-center">N/A</span>
                                    ) : (
                                      <input
                                        type="number"
                                        value={cabinet.height}
                                        onChange={(e) => updateCabinetField(index, 'height', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        placeholder="Height"
                                        min="0"
                                        step="0.1"
                                      />
                                    )}
                                  </td>
                                  
                                  {/* Color Column with Dropdown */}
                                  <td className="px-4 py-3">
                                    {isFixedPriceItem || isProfileHandle ? (
                                      <span className="text-sm text-gray-500 block text-center">N/A</span>
                                    ) : (
                                      <select
                                        value={cabinet.color || ''}
                                        onChange={(e) => updateCabinetField(index, 'color', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        disabled={isPVCRollingShutter && !cabinet.pvcSize}
                                      >
                                        <option value="">Select Color</option>
                                        {getAvailableColors(cabinet.type).map((color) => (
                                          <option key={color} value={color}>
                                            {color}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                  </td>
                                  
                                  <td className="px-4 py-3">
                                    <input
                                      type="text"
                                      value={cabinet.material}
                                      readOnly
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="number"
                                      value={cabinet.qty}
                                      onChange={(e) => updateCabinetField(index, 'qty', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      min="1"
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    {isFixedPriceItem || isPVCRollingShutter || isProfileHandle ? (
                                      <span className="text-sm text-gray-500 block text-center">N/A</span>
                                    ) : (
                                      <input
                                        type="number"
                                        value={cabinet.drawerSlides || '0'}
                                        onChange={(e) => updateCabinetField(index, 'drawerSlides', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        min="0"
                                      />
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {isFixedPriceItem || isPVCRollingShutter || isProfileHandle ? (
                                      <span className="text-sm text-gray-500 block text-center">N/A</span>
                                    ) : (
                                      <input
                                        type="number"
                                        value={cabinet.hinges || '0'}
                                        onChange={(e) => updateCabinetField(index, 'hinges', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        min="0"
                                      />
                                    )}
                                  </td>
                                </>
                              )}
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  value={cabinet.discount}
                                  onChange={(e) => updateCabinetField(index, 'discount', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  min="0"
                                  max="100"
                                />
                              </td>
                              <td className="px-4 py-3 font-semibold text-green-600">
                                ${calculateCabinetTotal(cabinet, selectedOrder.country).toFixed(2)}
                              </td>
                              <td className="px-4 py-3">
                                {selectedOrder.cabinets.length > 1 && (
                                  <button
                                    onClick={() => removeCabinetRow(index)}
                                    className="text-red-500 hover:text-red-700 transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEditDetailsModal(false);
                      setSelectedOrder(null);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEditedOrderDetails}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderList;